import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@material-tailwind/react";
import { ErrorOutline, Close } from "@mui/icons-material";
import { motion } from "framer-motion";

export function DialogCustomAnimation({
  handleClose,
  open,
  message = "",
  title = "",
  body = "",
  handleSave,
  handleDelete,
  header,
  size = "sm",
}) {
  return (
    <Dialog
    
      open={open}
      size={size}
      // handler={handleClose}
      animate={{
        mount: { scale: 1, y: 0, opacity: 1 },
        unmount: { scale: 0.95, y: -50, opacity: 0 },
      }}
      className="bg-white shadow-2xl border-0 rounded-2xl overflow-hidden"
    >
      {/* Enhanced Header */}
      {(title || header) && (
        <DialogHeader className="relative p-0 m-0">
          {title && (
            <div className="flex items-center gap-3 p-6 bg-gradient-to-r from-red-50 to-red-100 border-b border-red-200">
              <div className="p-2 bg-red-100 rounded-lg">
                <ErrorOutline className="text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-red-800">{title}</h2>
              </div>
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 hover:bg-red-200 rounded-lg transition-colors"
              >
                <Close className="w-5 h-5 text-red-600" />
              </button>
            </div>
          )}
          {header && !title && (
            <div className="relative brand-header text-white w-full">
              <div className="py-4 px-6 w-full">
                <h2 className="text-lg font-semibold text-center w-full">
                  {header}
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="absolute top-3 right-4 p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                <Close className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
        </DialogHeader>
      )}

      {/* Enhanced Body */}
      <DialogBody className="p-6 bg-white">
        <div className="space-y-3">
          {message && (
            <div className="text-gray-700 leading-relaxed text-center">
              {message}
            </div>
          )}
          {body && <div className="w-full">{body}</div>}
        </div>
      </DialogBody>

      {/* Enhanced Footer */}
      <DialogFooter className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 border-t border-gray-200">
        <div className="flex gap-3 justify-end w-full">
          {/* Cancel Button */}
          <motion.div 
            whileHover={{ scale: 1.05, y: -2 }} 
            whileTap={{ scale: 0.95 }}
            className="relative overflow-hidden rounded-lg group"
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 bg-white hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:border-gray-400 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden"
            >
              <span className="relative z-10">CANCEL</span>
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-500 ease-in-out bg-gradient-to-r from-transparent via-white/30 to-transparent"></span>
            </Button>
          </motion.div>

          {/* Delete Button */}
          {handleDelete && (
            <motion.div 
              whileHover={{ scale: 1.05, y: -2 }} 
              whileTap={{ scale: 0.95 }}
              className="relative overflow-hidden rounded-lg group"
            >
              <Button
                variant="filled"
                onClick={handleDelete}
                className="px-6 py-2.5 bg-gradient-to-r from-red-600 via-red-600 to-red-700 text-white hover:from-red-700 hover:via-red-700 hover:to-red-800 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden"
              >
                <span className="relative z-10">DELETE</span>
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-500 ease-in-out bg-gradient-to-r from-transparent via-white/20 to-transparent"></span>
              </Button>
            </motion.div>
          )}

          {/* Save Button */}
          {handleSave && (
            <motion.div 
              whileHover={{ scale: 1.05, y: -2 }} 
              whileTap={{ scale: 0.95 }}
              className="relative overflow-hidden rounded-lg group"
            >
              <Button
                variant="filled"
                onClick={handleSave}
                className="px-6 py-2.5 bg-gradient-to-r from-[#0b3a5c] via-[#135e96] to-[#0b3a5c] text-white hover:from-[#135e96] hover:via-[#0b3a5c] hover:to-[#135e96] rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden"
              >
                <span className="relative z-10">SAVE</span>
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-500 ease-in-out bg-gradient-to-r from-transparent via-white/20 to-transparent"></span>
              </Button>
            </motion.div>
          )}
        </div>
      </DialogFooter>
    </Dialog>
  );
}

DialogCustomAnimation.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  message: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.node,
  handleSave: PropTypes.func,
  handleDelete: PropTypes.func,
  header: PropTypes.node,
  size: PropTypes.string,
};

DialogCustomAnimation.defaultProps = {
  message: "",
  title: "",
  body: "",
  handleSave: undefined,
  handleDelete: undefined,
  header: undefined,
  size: "sm",
};
